import * as React from 'react';
import clsx from 'clsx';
import { ArrowPathIcon, PhotoIcon, VideoCameraIcon } from '@heroicons/react/24/solid';
import { useAuth } from '@/hooks/useAuth';
import { getFullImageUrl } from '@/utils/url';
import { AudioPlayerMock } from './AudioPlayerMock';
import { getUserFullName } from '@/utils/formatters';
import { ConfirmModal } from '@/components/chat/ChatHeader';
import { useEchoProfileCard } from '../hooks/useEchoProfileCard';
import { featureFlags } from '@/utils/featureFlags';

// Assets
import noEchoImage from '@/assets/images/echo/no_echo.webp';

interface EchoProfileCardProps {
  name?: string;
  imageSrc?: string;
  idleVideoUrl?: string | null | undefined;
  draftId?: string | undefined;
  echoId?: string;
  onDelete?: () => void;
  isDeleting?: boolean;
}

export const EchoProfileCard: React.FC<EchoProfileCardProps> = ({
  name: propName,
  imageSrc: propImage,
  idleVideoUrl,
  draftId,
  echoId,
  onDelete,
  isDeleting = false,
}) => {
  const { user } = useAuth();

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const storageKey = echoId || draftId ? `echo-avatar-mode:${echoId || draftId}` : null;

  const {
    videoMode,
    persistMode,
    variants,
    isGeneratingIdle,
    idleGenError,
    idleGenStarted,
    liveIdleVideoUrl,
    handleGenerateIdle,
  } = useEchoProfileCard({ draftId, idleVideoUrl, storageKey });

  React.useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  // Fallback logic: Prop -> User Name (with legacy support) -> Default
  const name = propName || (user ? getUserFullName(user) : 'My Echo');

  // Fallback logic: Prop -> User Image -> Placeholder (resolved to absolute URL)
  const rawImageSrc = propImage || user?.profile_picture_url || noEchoImage;
  const finalImageSrc = React.useMemo(() => getFullImageUrl(rawImageSrc), [rawImageSrc]);

  // Resolve active video URL based on mode
  const activeVideoUrl = React.useMemo(() => {
    if (videoMode === 'photo') return null;
    if (videoMode === 'idle') return liveIdleVideoUrl || idleVideoUrl || null;
    // variant-N
    const variantNum = parseInt(videoMode.split('-')[1] ?? '0', 10);
    return variants.find((variant) => variant.variant_number === variantNum)?.video_url ?? null;
  }, [videoMode, liveIdleVideoUrl, idleVideoUrl, variants]);

  const hasIdleVideo = !!(liveIdleVideoUrl || idleVideoUrl);
  const hasAnyVideo = hasIdleVideo || variants.length > 0;

  return (
    <>
      <div className={clsx('flex flex-col overflow-hidden')}>
        {/* Image Container */}
        <div className={clsx('relative w-full overflow-hidden', 'max-h-[388px]')}>
          {/* Profile Image or Video Avatar */}
          {featureFlags.videoEnabled && activeVideoUrl ? (
            <video
              ref={videoRef}
              src={activeVideoUrl}
              className='h-[388px] w-full object-cover object-top'
              autoPlay
              loop
              playsInline
              muted
            />
          ) : (
            <img
              src={finalImageSrc}
              alt={name}
              className='max-h-[388px] w-full object-cover object-top'
              loading='lazy'
            />
          )}

          {/* Arrow Button (top-right) */}
          {(onDelete || draftId) && (
            <div ref={menuRef} className='absolute right-0 top-0 z-30'>
              <div
                role='button'
                tabIndex={0}
                onClick={() => setIsMenuOpen((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsMenuOpen((prev) => !prev);
                  }
                }}
                className='flex cursor-pointer items-center justify-center rounded-bl-lg bg-gray-350 px-[6px] py-[10px]'
                aria-label='Echo options'
              >
                <svg width='10' height='6' viewBox='0 0 10 6' fill='none'>
                  <path
                    d='M1 1l4 4 4-4'
                    stroke='#1A1036'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>

              {isMenuOpen && (
                <div className='absolute right-0 top-full z-30 mt-1 w-48 overflow-hidden rounded-lg bg-white shadow-lg'>
                  {/* Photo mode */}
                  {draftId && (
                    <button
                      type='button'
                      className={clsx(
                        'flex w-full items-center gap-2 px-3 py-2.5 text-left font-sans text-[13px] font-medium transition-colors hover:bg-gray-50',
                        videoMode === 'photo' ? 'text-brand-600' : 'text-brand-800'
                      )}
                      onClick={() => {
                        setIsMenuOpen(false);
                        persistMode('photo');
                      }}
                    >
                      <PhotoIcon className='size-4' />
                      Static Image
                      {videoMode === 'photo' && (
                        <span className='ml-auto text-[11px]'>&#10003;</span>
                      )}
                    </button>
                  )}

                  {/* Idle animation */}
                  {featureFlags.videoEnabled && draftId && (
                    <button
                      type='button'
                      className={clsx(
                        'flex w-full items-center gap-2 px-3 py-2.5 text-left font-sans text-[13px] font-medium transition-colors hover:bg-gray-50 disabled:opacity-50',
                        videoMode === 'idle' ? 'text-brand-600' : 'text-brand-800'
                      )}
                      disabled={isGeneratingIdle}
                      onClick={() => {
                        setIsMenuOpen(false);
                        if (hasIdleVideo) {
                          persistMode('idle');
                        } else {
                          handleGenerateIdle();
                        }
                      }}
                    >
                      <VideoCameraIcon className='size-4' />
                      {hasIdleVideo
                        ? 'Idle Animation'
                        : isGeneratingIdle || idleGenStarted
                          ? 'Generating...'
                          : 'Generate Idle'}
                      {videoMode === 'idle' && hasIdleVideo && (
                        <span className='ml-auto text-[11px]'>&#10003;</span>
                      )}
                    </button>
                  )}

                  {/* Video variants */}
                  {featureFlags.videoEnabled &&
                    variants.map((variant) => (
                      <button
                        key={variant.variant_number}
                        type='button'
                        className={clsx(
                          'flex w-full items-center gap-2 px-3 py-2.5 text-left font-sans text-[13px] font-medium transition-colors hover:bg-gray-50',
                          videoMode === `variant-${variant.variant_number}`
                            ? 'text-brand-600'
                            : 'text-brand-800'
                        )}
                        onClick={() => {
                          setIsMenuOpen(false);
                          persistMode(`variant-${variant.variant_number}`);
                        }}
                      >
                        <VideoCameraIcon className='size-4' />
                        Greeting {variant.variant_number}
                        {videoMode === `variant-${variant.variant_number}` && (
                          <span className='ml-auto text-[11px]'>&#10003;</span>
                        )}
                      </button>
                    ))}

                  {/* Separator before actions */}
                  {featureFlags.videoEnabled &&
                    hasAnyVideo &&
                    (onDelete || (draftId && hasIdleVideo)) && (
                      <div className='mx-2 border-t border-gray-100' />
                    )}

                  {/* Regenerate idle */}
                  {featureFlags.videoEnabled && draftId && hasIdleVideo && (
                    <button
                      type='button'
                      className='flex w-full items-center gap-2 px-3 py-2.5 text-left font-sans text-[13px] font-medium text-brand-800 transition-colors hover:bg-gray-50 disabled:opacity-50'
                      disabled={isGeneratingIdle || idleGenStarted}
                      onClick={() => {
                        setIsMenuOpen(false);
                        handleGenerateIdle();
                      }}
                    >
                      <ArrowPathIcon className='size-4' />
                      {isGeneratingIdle || idleGenStarted ? 'Regenerating...' : 'Regenerate Idle'}
                    </button>
                  )}

                  {/* Delete */}
                  {onDelete && (
                    <button
                      type='button'
                      disabled={isDeleting}
                      className='flex w-full items-center gap-2 px-3 py-2.5 text-left font-sans text-[13px] font-medium text-red-500 transition-colors hover:bg-gray-50 disabled:opacity-50'
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsDeleteOpen(true);
                      }}
                    >
                      <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                        <path
                          d='M1.75 3.5h10.5M5.25 6.125v3.5M8.75 6.125v3.5M2.625 3.5l.583 7.583a1.167 1.167 0 001.167 1.042h5.25a1.167 1.167 0 001.167-1.042l.583-7.583M4.667 3.5V2.333A1.167 1.167 0 015.833 1.167h2.334A1.167 1.167 0 019.333 2.333V3.5'
                          stroke='currentColor'
                          strokeWidth='1.2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                      Delete Echo
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Idle generation loading overlay */}
          {featureFlags.videoEnabled && idleGenStarted && (
            <div className='absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/40 backdrop-blur-sm'>
              <div className='size-10 animate-spin rounded-full border-4 border-white/30 border-t-white' />
              <p className='font-sans text-14 font-semibold text-white'>
                Creating Idle Animation...
              </p>
              <p className='font-sans text-12 font-normal text-white/70'>This may take a minute</p>
            </div>
          )}

          {/* Deleting overlay */}
          {isDeleting && (
            <div className='absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/40 backdrop-blur-sm'>
              <div className='size-10 animate-spin rounded-full border-4 border-white/30 border-t-white' />
              <p className='font-sans text-14 font-semibold text-white'>Deleting Echo...</p>
            </div>
          )}

          {/* Bottom Gradient Overlay */}
          <div className='pointer-events-none absolute bottom-0 left-0 z-20 h-[30%] w-full bg-gradient-overlay-10' />
        </div>

        {/* Content */}
        <div className='flex flex-col px-6 pb-6'>
          <h2
            className={clsx(
              'mb-6 text-nowrap text-center font-proxima font-extrabold uppercase text-dark-100',
              // Responsive Typography: 24px on mobile, 40px on desktop (md+)
              'text-24 leading-47 md:text-40'
            )}
          >
            {name}
          </h2>

          <div className='flex w-full flex-col gap-6'>
            <AudioPlayerMock {...(draftId ? { draftId } : {})} {...(echoId ? { echoId } : {})} />
          </div>

          {featureFlags.videoEnabled && (idleGenStarted || idleGenError) && (
            <div className='mt-3 flex flex-col items-center gap-1'>
              {idleGenStarted && (
                <p className='flex items-center gap-2 font-sans text-13 font-medium text-brand-600'>
                  <span className='inline-block size-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent' />
                  Generating idle video... It will appear automatically.
                </p>
              )}
              {idleGenError && <p className='font-sans text-13 text-red-500'>{idleGenError}</p>}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={() => {
          setIsDeleteOpen(false);
          onDelete?.();
        }}
        title='Delete Echo?'
        message={
          <>
            Are you sure you want to delete your echo?{' '}
            <span className='font-bold'>This action cannot be undone.</span>
          </>
        }
        confirmLabel='Delete'
      />
    </>
  );
};
